import { useContext, useEffect, useRef, useState } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';
import { Section } from 'types/sections';

import CmsSection from 'models/CmsSection';
import Location from 'models/Location';

import { withVariables } from 'utils/string';

import { single } from 'requests/locations';

import LocaleContext from 'components/context/Locale';
import Breadcrumbs from 'components/layout/Breadcrumbs/component';
import Head from 'components/layout/Head';
import PageContainer from 'components/layout/PageContainer';
import SectionsWrapper from 'components/modules/public/cms/SectionsWrapper';
import SectionWrapper from 'components/modules/public/cms/SectionWrapper';
import PageNotFound from 'components/modules/public/pages/NotFound';
import WrapperPublic from 'components/wrappers/Public';

import SectionBodyPartGroups from './sections/BodyPartGroups';
import SectionCheckout from './sections/Checkout';
import SectionContact from './sections/Contact';
import SectionHero from './sections/Hero';
import SectionLocation from './sections/Location';
import SectionLocationInfo from './sections/LocationInfo';
import SectionLocations from './sections/Locations';
import SectionSeoDescription from './sections/SeoDescription';
import StyledComponent from './styles';
import { Props } from './types';

const PublicPageLocation: NextPage<Props> = ({
    notFound,
    location,
    cmsPage,
    locationsNearby,
}) => {
    if (notFound) return <PageNotFound />;
    const isMounted = useRef<boolean>(false);
    const router = useRouter();
    const { categorySlug } = router.query;
    const localeContext = useContext(LocaleContext);
    const translations = localeContext?.translations?.location;
    const translationsBreadcrumbs = localeContext?.translations?.breadcrumbs;
    const [locationState, setLocationState] = useState<Location>(location);

    const selectedProductCategory = locationState?.productCategories?.find(
        category => category.locales?.[0]?.slug === categorySlug
    );

    const selectedLocationLocale = selectedProductCategory
        ? locationState?.locales?.find(locale => locale.productCategoryId === selectedProductCategory.id) || locationState?.locale
        : locationState?.locale;

    const breadcrumbs = [{
        to: Routes.PublicHome,
        label: translationsBreadcrumbs.home,
    }, {
        to: Routes.PublicLocations,
        label: translationsBreadcrumbs.locations,
    }, {
        to: withVariables(Routes.PublicCategoryLocations, { categorySlug: selectedProductCategory?.locales[0]?.slug  }),
        label: selectedProductCategory?.nameDisplay,
    }, {
        to: withVariables(Routes.PublicLocation, { slug: selectedLocationLocale?.slug }),
        label: selectedLocationLocale?.name,
        active: true,
    }];

    // Remove current location
    locationsNearby.elements = locationsNearby?.elements.filter(locationNearby => locationNearby.id !== locationState.id);

    useEffect(() => {
        if (isMounted.current) {
            single({ slug: router?.query?.slug.toString() })
                .then(response => setLocationState(new Location(response.data)));
        } else {
            isMounted.current = true;
        }
    }, [router?.query?.slug]);

    const sections: Section[] = [
        {
            slug: 'hero',
            order: 1,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionHero
                            translations={{
                                ...translations?.content?.hero,
                                ...locale,
                            }}
                            override={() => override({
                                translations: {
                                    ...locale,
                                    headline: `<h1>${selectedLocationLocale?.headlineSeo || selectedLocationLocale?.name}</h1>`,
                                },
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'breadcrumbs',
            order: 2,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (<Breadcrumbs breadcrumbs={breadcrumbs} />)}
                </SectionWrapper>
            ),
        },
        {
            slug: 'location',
            order: 3,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => {
                        return (
                            <SectionLocation
                                key={locationState.id}
                                override={() => override({
                                    location: locationState,
                                })}
                            />
                        );
                    }}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locationInfo',
            order: 4,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocationInfo override={() => override({
                            infos: locationState?.infos,
                        })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'seoDescription',
            order: 5,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionSeoDescription
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'packages',
            order: 6,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionBodyPartGroups
                            override={() => override({
                                initialLocationId: location?.id,
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'locations',
            order: 7,
            visible: Array.isArray(locationsNearby?.elements) && locationsNearby.elements.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionLocations
                            locale={locale}
                            override={() => override({
                                locations: locationsNearby?.elements || [],
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'contact',
            order: 8,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionContact
                            locale={locale}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'checkout',
            order: 9,
            visible: Boolean(selectedLocationLocale?.description),
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (
                        <SectionCheckout
                            location={locationState}
                            {...translations?.content?.checkout}
                            locale={selectedLocationLocale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
    ];

    return (
        <WrapperPublic
            isBannerVisible={cmsPage?.data?.isBannerVisible}
            bannerContent={cmsPage?.data?.bannerContent}
            bannerLink={cmsPage?.data?.bannerLink}
        >
            <StyledComponent className="public-page-location">
                <Head
                    titleSuffix=""
                    title={selectedLocationLocale?.metaTitle || cmsPage?.data?.locale?.title || translations?.meta?.title}
                    description={selectedLocationLocale?.metaDescription || cmsPage?.data?.locale?.description || translations?.meta?.description}
                    keywords={cmsPage?.data?.locale?.keywords || translations?.meta?.keywords}
                    tags={cmsPage?.data?.head}
                    cmsPageIndexingEnabled={cmsPage?.data?.indexingEnabled}
                />
                <PageContainer>
                    <SectionsWrapper
                        memoRefresher
                        sections={sections}
                        cmsSections={cmsPage?.data?.sections}
                    />
                </PageContainer>
            </StyledComponent>
        </WrapperPublic>
    );
};

export default PublicPageLocation;
